import { useEffect, useState } from 'react'

import { ChainId } from '@src/configs/chainInfo'
import { usePrecomputedEVMGasPrices } from '@src/hooks/evm/usePrecomputedEVMGasPrices'
import { viemChainConfigs } from '@src/hooks/evm/useViemClient'
import { usePrecomputedSolanaGasPrices } from '@src/hooks/solana/usePrecomputedSolanaGasPrices'
import { usePrecomputedTronGasPrices } from '@src/hooks/tron/usePrecomputedTronGasPrices'
import { PrecomputeGasResults } from '@src/types/gasPrices'
import { createContextLogger } from '@src/utils/logger'
import { useConfigStore } from '@src/zustand_stores/config.store'

const PRINT_GAS_ERRORS = false
const logger = createContextLogger('usePrecomputedGasPrices')

/**
 * Custom hook to precompute gas prices/fees of transactions
 * @returns Precomputed gas prices
 */
export const usePrecomputedGasPrices = (swapContracts: Record<string, any> | undefined) => {
    const [isComputing, setIsComputing] = useState(false)
    const [isGasPrecomputingDone, setIsGasPrecomputingDone] = useState(false)
    const [precomputedGasPrices, setPrecomputedGasPrices] = useState<PrecomputeGasResults>()

    // Get available networks from config store
    const availableNetworks = useConfigStore(state => state.availableNetworks)

    // Only initialize hooks for available chain types
    const { computeEVMGasPrices } = usePrecomputedEVMGasPrices(swapContracts)
    const { computeSolanaGasPrices } = usePrecomputedSolanaGasPrices()
    const { computeTronGasPrices } = usePrecomputedTronGasPrices()

    // Check if specific chain types are available in the networks
    const hasEvmChains = Object.keys(viemChainConfigs).length > 0
    const hasSolana = Object.values(availableNetworks).some(network => network.chainId === ChainId.Solana)
    const hasTron = Object.values(availableNetworks).some(network => network.chainId === ChainId.Tron)

    useEffect(() => {
        setIsComputing(true)
        setIsGasPrecomputingDone(false)
        setPrecomputedGasPrices(undefined)

        const precomputeGasFees = async () => {
            if (!swapContracts) {
                PRINT_GAS_ERRORS && logger.error('swapContracts not found')
                return
            }

            try {
                // Setup computations for all chain types, but only process those that are available
                const computePromises = []
                let evmResults = {},
                    solanaResults = {},
                    tronResults = {}

                // For each chain type, either compute or use an empty object
                computePromises.push(
                    hasEvmChains
                        ? computeEVMGasPrices().then(result => {
                              evmResults = result
                          })
                        : Promise.resolve()
                )
                computePromises.push(
                    hasSolana
                        ? computeSolanaGasPrices().then(result => {
                              solanaResults = result
                          })
                        : Promise.resolve()
                )
                computePromises.push(
                    hasTron
                        ? computeTronGasPrices().then(result => {
                              tronResults = result
                          })
                        : Promise.resolve()
                )

                // Wait for all computations to complete
                await Promise.all(computePromises)

                const results: PrecomputeGasResults = {
                    ...evmResults,
                    ...solanaResults,
                    ...tronResults,
                }

                // logger.info('Precomputed gas fee results:', results)
                setPrecomputedGasPrices(results)
            } catch (error) {
                PRINT_GAS_ERRORS && logger.error('Error computing gas prices:', error)
            } finally {
                setIsComputing(false)
                setIsGasPrecomputingDone(true)
            }
        }

        precomputeGasFees()
    }, [swapContracts, viemChainConfigs, availableNetworks, hasEvmChains, hasSolana, hasTron])

    return { precomputedGasPrices, isComputing, isGasPrecomputingDone }
}
